import { useState, useEffect } from "react";
import Header from "@/components/layout/Header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, CheckCircle, Smartphone, Globe, Zap } from "lucide-react";
import { toast } from "sonner";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function Install() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      toast.info('Install option not available. Use your browser menu to install.');
      return;
    }

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        toast.success('App installed successfully!');
        setIsInstalled(true);
      }
      
      setDeferredPrompt(null);
    } catch (error) {
      toast.error('Failed to install app');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container py-8 max-w-4xl">
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold mb-4 text-gradient">Install the App</h1>
          <p className="text-lg text-muted-foreground">
            Get quick access and offline support by installing our PWA
          </p>
        </div>

        {isInstalled ? (
          <Card className="mb-8 border-success">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4 text-success">
                <CheckCircle className="h-12 w-12" />
                <div>
                  <h3 className="text-xl font-semibold mb-1">App Already Installed</h3>
                  <p className="text-muted-foreground">
                    You're all set! The app is installed on your device.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="h-6 w-6 text-primary" />
                Quick Install
              </CardTitle>
              <CardDescription>
                Install with one click (if supported by your browser)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={handleInstall} 
                className="w-full ocean-gradient gap-2"
                size="lg"
              >
                <Download className="h-5 w-5" />
                Install App Now
              </Button>
            </CardContent>
          </Card>
        )}

        <div className="grid gap-6 md:grid-cols-3 mb-8">
          <Card>
            <CardHeader>
              <Zap className="h-10 w-10 text-primary mb-2" />
              <CardTitle className="text-lg">Fast Access</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Launch directly from your home screen like a native app
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <Globe className="h-10 w-10 text-primary mb-2" />
              <CardTitle className="text-lg">Offline Support</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Access your catches and record new ones even without internet
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <Smartphone className="h-10 w-10 text-primary mb-2" />
              <CardTitle className="text-lg">Native Feel</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Works seamlessly on all devices with a native app experience
              </p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Manual Installation Instructions</CardTitle>
            <CardDescription>
              If the automatic install doesn't work, follow these steps:
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Smartphone className="h-5 w-5" />
                iOS (iPhone/iPad)
              </h3>
              <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground ml-7">
                <li>Open this website in Safari browser</li>
                <li>Tap the Share button (square with arrow pointing up)</li>
                <li>Scroll down and tap "Add to Home Screen"</li>
                <li>Tap "Add" in the top right corner</li>
              </ol>
            </div>

            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Smartphone className="h-5 w-5" />
                Android (Chrome)
              </h3>
              <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground ml-7">
                <li>Open this website in Chrome browser</li>
                <li>Tap the menu button (three dots) in the top right</li>
                <li>Tap "Install app" or "Add to Home screen"</li>
                <li>Tap "Install" to confirm</li>
              </ol>
            </div>

            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Desktop (Chrome/Edge)
              </h3>
              <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground ml-7">
                <li>Click the install icon in the address bar (or menu)</li>
                <li>Click "Install" in the popup</li>
                <li>The app will open in its own window</li>
              </ol>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
